import React, {
  FC,
  SelectHTMLAttributes,
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
  useRef,
  useMemo,
} from 'react';
import { Listbox, Transition } from '@headlessui/react';
import {
  CaretDown,
  MultiSelectIconBlank,
  MultiSelectIconFilled,
  MultiSelectDisabledIcon,
  Icon,
  RemoveTag,
} from '..';
import ErrorMessage from '../error-message/error-message';

type OptionType = {
  name: string;
  value: string;
};

export interface MultiSelectProps
  extends SelectHTMLAttributes<HTMLSelectElement> {
  selectLabel: string;
  options: OptionType[];
  width?: 'full' | 'lg' | 'md';
  positioning?: 'relative' | 'absolute';
  selectedOptions?: OptionType[];
  setSelectedOptions?: Dispatch<SetStateAction<OptionType[]>>;
  onChange?: any;
  errors?: any;
  name?: string;
  withLabel?: boolean;
  isValid?: boolean;
  errorMessage?: string;
  largeHeight?: boolean;
  disabled?: boolean;
  maximum?: number;
  disableSelectAll?: boolean;
}

export const MultiSearchSelect: FC<MultiSelectProps> = ({
  className,
  selectLabel,
  options = [],
  positioning,
  selectedOptions = [],
  setSelectedOptions,
  width,
  errors,
  name,
  withLabel,
  isValid = true,
  errorMessage,
  largeHeight,
  disabled,
  maximum,
  disableSelectAll,
}: MultiSelectProps) => {
  const ref = useRef<any>();
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedList, setSelectedList] =
    useState<OptionType[]>(selectedOptions);
  const [label, setLabel] = useState<string>(selectLabel);
  const [visible, setVisible] = useState<boolean>(false);
  const [isFocused, setFocused] = useState<boolean>(false);
  const [inputFocus, setInputFocus] = useState<boolean>(false);
  const [filteredOptions, setFilteredOptions] = useState<OptionType[]>(options);
  const [search, setSearch] = useState<string>('');

  const handleOnclick = useMemo(
    () =>
      (item: OptionType, e: React.SyntheticEvent<EventTarget>): void => {
        e.preventDefault();
        const isAll = item.name.toLowerCase() === 'all';

        const doesContainOption = selectedList.some(
          (option) => option.value === item.value
        );

        const itemAdded = isAll ? [...options] : [...selectedList, item];

        const itemRemoved = !isAll
          ? selectedList.filter(
              (option) =>
                option.value !== item.value &&
                option.name.toLowerCase() !== 'all'
            )
          : [];

        const newList = doesContainOption ? itemRemoved : itemAdded;
        setSelectedList(newList);
      },
    [selectedList]
  );

  const handleListChange = (item: any) => {
    if (!disabled) {
      const isAll = item.name.toLowerCase() === 'all';

      const doesContainOption =
        selectedList.some((option) => option.value === item.value) ||
        (isAll && selectedList?.length === options?.length);

      let itemAdded;
      if (maximum) {
        if (selectedList.length < maximum) {
          itemAdded = isAll ? [...options] : [...selectedList, item];
        } else {
          itemAdded = [...selectedList];
        }
      } else {
        itemAdded = isAll ? [...options] : [...selectedList, item];
      }

      const itemRemoved = !isAll
        ? selectedList.filter(
            (option) =>
              option.value !== item.value && option.name.toLowerCase() !== 'all'
          )
        : [];
      const newList = doesContainOption ? itemRemoved : itemAdded;
      setSelectedList(newList);
      setSelectedOptions && setSelectedOptions(newList);
    }
  };

  const handleSelectClick = (e: React.MouseEvent) => {
    const target = e?.target as HTMLInputElement;

    if (target.classList?.contains('tag')) {
      return setVisible(visible);
    }

    if (target?.classList?.length) setVisible(!visible);
  };

  useEffect(() => {
    const handleKeyOpen = (e: KeyboardEvent) => {
      if (isFocused && e.key === 'Enter') {
        e.preventDefault();
        setVisible(true);
      }
    };

    document.addEventListener('keydown', handleKeyOpen);

    return () => {
      document.removeEventListener('keydown', handleKeyOpen);
    };
  }, [isFocused]);

  useEffect(() => {
    const handleMouseDownClose = (e: MouseEvent) => {
      if (visible && ref.current && !ref.current.contains(e.target)) {
        setVisible(false);
      }
    };

    const handleKeyDownClose = (e: KeyboardEvent) => {
      if (visible && e.key === 'Escape') {
        setVisible(false);
      }
    };

    setSelectedOptions && setSelectedOptions(selectedList);

    document.addEventListener('keydown', handleKeyDownClose);
    document.addEventListener('mousedown', handleMouseDownClose);
    return () => {
      document.removeEventListener('keydown', handleKeyDownClose);
      document.removeEventListener('mousedown', handleMouseDownClose);
    };
  }, [selectedList, visible, label]);

  useEffect(() => setLabel(selectLabel), [selectLabel]);

  useEffect(() => {
    setSelectedList && setSelectedList(selectedOptions);
  }, [selectedOptions?.length]);

  useEffect(() => {
    setFilteredOptions(options);
  }, [options?.length]);

  useEffect(() => {
    const selectedOptionsDiv = document.getElementById('selected-options');
    const wrapper = document.getElementById('wrapper');
    const list = document.querySelector('.list');
    let scrollingOptions = false;

    if (
      list &&
      selectedOptionsDiv &&
      list?.getBoundingClientRect()?.bottom -
        selectedOptionsDiv?.getBoundingClientRect()?.bottom >
        0
    ) {
      wrapper?.classList.add('off-bottom');
    } else {
      wrapper?.classList.remove('off-bottom');
    }
    const setShadows = (e: Event) => {
      const target = e?.target as HTMLInputElement;
      if (!scrollingOptions) {
        window.requestAnimationFrame(function () {
          if (target?.scrollTop > 0) {
            wrapper?.classList.add('off-top');
          } else {
            wrapper?.classList.remove('off-top');
          }

          if (
            list &&
            selectedOptionsDiv &&
            list?.getBoundingClientRect()?.bottom -
              selectedOptionsDiv?.getBoundingClientRect()?.bottom >
              0
          ) {
            wrapper?.classList.add('off-bottom');
          } else {
            wrapper?.classList.remove('off-bottom');
          }

          scrollingOptions = false;
        });
        scrollingOptions = true;
      }
      return scrollingOptions;
    };

    if (selectedOptionsDiv)
      selectedOptionsDiv?.addEventListener('scroll', setShadows);
  }, [
    document.querySelector('#selected-options'),
    document.querySelector('.list')?.clientHeight,
  ]);

  const handleSearchChange = (e: any) => {
    const { value } = e.target;
    e.stopPropagation();
    setSearch(value);

    if (value.length) {
      const filteredOptionsBySearch = options?.filter((option) =>
        option?.name?.toLowerCase().includes(value.toLowerCase())
      );

      setFilteredOptions(filteredOptionsBySearch);
    } else {
      setFilteredOptions(options);
    }
  };

  const removeTags = (value: string) => {
    setSelectedList([
      ...selectedOptions.filter((option) => option.value !== value),
    ]);
  };

  const handleSearchFocus = (e: MouseEvent) => {
    const inputNode = inputRef.current;
    if (inputNode?.contains(e.target as HTMLInputElement)) {
      setInputFocus(true);
    } else setInputFocus(false);
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleSearchFocus);

    return () => {
      document.removeEventListener('mousedown', handleSearchFocus);
    };
  }, []);

  return (
    <div
      ref={ref}
      className={`relative ${
        width === 'full'
          ? 'w-full'
          : width === 'lg'
          ? 'w-1/2'
          : width === 'md'
          ? 'w-52'
          : 'w-44'
      } ${className ? className : ''}`}
    >
      <Listbox
        as="div"
        value={selectedList}
        className={`text-left cursor-pointer`}
        onChange={(value: any) => {
          handleListChange(value);
        }}
        aria-label="Multi-Select"
      >
        <>
          <Listbox.Button
            as="div"
            className={`w-full peer relative  ${
              visible
                ? 'border-b-2 border-thryv-black-500'
                : 'border-b text-thryv-steel border-thryv-gray-light-500'
            } transition-all`}
            onMouseDown={(e: React.MouseEvent) => handleSelectClick(e)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            tabIndex={0}
            data-testid="multiselect"
            role="button"
          >
            <div>
              <span
                className={`absolute ${
                  selectedOptions.length || visible || withLabel
                    ? '-top-5 opacity-100'
                    : 'top-0 opacity-0'
                } left-0 ${
                  errors && name && errors[name] && errors[name].message
                    ? 'text-utility-error-danger'
                    : 'text-thryv-steel'
                } text-label-text mb-2 transition-all duration-400`}
              >
                {label}
              </span>
              <div className="mt-2 flex justify-between items-end relative ">
                <div
                  className="w-11/12 truncate"
                  data-testid="multisearchselect-options"
                  id="wrapper"
                >
                  {selectedOptions.length ? (
                    <div
                      className=" max-h-[55px] overflow-y-scroll"
                      id="selected-options"
                    >
                      <ul
                        className={`flex flex-wrap justify-start items-start gap-2 mt-2 mb-2 list`}
                        aria-label="Entered tags"
                      >
                        {selectedList.map((option, i) => (
                          <li
                            className="flex px-2 py-[3.5px] justify-start items-center gap-1 text-thryv-black-500 
                text-label-text font-open-sans capitalize font-normal rounded-lg bg-thryv-white-300 transition-all duration-200 ease-in-out"
                            key={i}
                            aria-label={`Tag: ${option.name}`}
                          >
                            <span className="w-full">{option.name}</span>
                            {!disabled && (
                              <button
                                type="button"
                                onClick={() => removeTags(option.value)}
                              >
                                <RemoveTag className="cursor-pointer mt-[0.5px] outline-none focus-visible:ring-2 tag">
                                  <title>
                                    Click to remove the {option.name} tag
                                  </title>
                                </RemoveTag>
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                      <div className="absolute w-10/12 bottom-0 tags-shadow-bottom shadow" />
                      <div className="absolute w-10/12 top-0  tags-shadow-top shadow" />
                    </div>
                  ) : !visible ? (
                    label!.length > 0 ? (
                      <>
                        {selectedOptions.length === 0 ? (
                          <span className="text-thryv-steel text-form-text mb-5 transition-all">
                            {label}
                          </span>
                        ) : null}
                      </>
                    ) : (
                      <span className="text-thryv-black-500 text-p-lg mb-2 transition-all">
                        -- Select --
                      </span>
                    )
                  ) : (
                    <span className="text-thryv-black-500 text-p-lg mb-2 transition-all">
                      -- Select --
                    </span>
                  )}
                </div>
                <CaretDown
                  className={`fill-current mb-2 pointer-events-none ${
                    visible ? 'text-thryv-black-500' : ''
                  }`}
                />
              </div>
            </div>
          </Listbox.Button>

          <Transition
            as="div"
            show={visible}
            enter="transition ease-in duration-300 relative z-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-out duration-200"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <>
              <Listbox.Options
                className={`overflow-auto ${
                  largeHeight ? 'h-64' : 'h-32'
                } w-full mt-3 origin-top-left bg-white rounded shadow-thryv focus:outline-none ${
                  positioning || 'absolute'
                } z-100`}
                static
              >
                <div className="px-3 pt-3 pb-1 mb-2 sticky top-0 bg-white z-10">
                  <div
                    onClick={() => setInputFocus(true)}
                    onBlur={() => setInputFocus(false)}
                    className={`w-full flex justify-between items-center px-0 py-2 border ${
                      !inputFocus
                        ? ' border-thryv-gray-light-500 placeholder-thryv-steel'
                        : 'border-thryv-black-500  ring-1 ring-thryv-black-500'
                    } rounded transition-all ${
                      (errors &&
                        name &&
                        errors[name] &&
                        errors[name].message) ||
                      !isValid
                        ? 'border-utility-error-danger peer-focus:border-utility-error-danger'
                        : ''
                    } ${
                      inputFocus &&
                      ((errors &&
                        name &&
                        errors[name] &&
                        errors[name].message) ||
                        !isValid)
                        ? 'border-utility-error-danger peer-focus:border-utility-error-danger ring-1 ring-utility-error-danger'
                        : ''
                    }`}
                  >
                    <span className="absolute h-full right-4 flex items-center pointer-events-none">
                      <Icon
                        type="solid"
                        variant="search"
                        color={
                          (errors &&
                            name &&
                            errors[name] &&
                            errors[name].message) ||
                          !isValid
                            ? '#DF2A2A'
                            : !inputFocus
                            ? '#A3A5A7'
                            : '#231F20'
                        }
                        height="17px"
                        role="img"
                      >
                        <title>Magnifying Glass Icon</title>
                      </Icon>
                    </span>
                    <input
                      type="text"
                      name="search"
                      placeholder="Search"
                      className={`w-3/4 peer appearance-none 
            text-thryv-black-500 text-form-text border-none ring-0 focus:outline-none pl-4 py-0 bg-transparent`}
                      autoComplete="off"
                      onChange={(e) => handleSearchChange(e)}
                      onKeyDown={(e) => e.stopPropagation()}
                      value={search}
                      ref={inputRef}
                      //   onKeyUp={handleKeypress}
                      //   onBlur={onBlur && onBlur}
                      //   autoComplete="off"
                      //   readOnly={readOnly || false}
                      //   {...rest}
                    />
                  </div>
                </div>
                <div className="pb-[4px]">
                  {filteredOptions.map((option, i) => (
                    <div key={i}>
                      <Listbox.Option value={option}>
                        {({ active }) =>
                          disabled ? (
                            <button
                              className={` flex justify-start items-center ${
                                active &&
                                'bg-thryv-white-300 transition-all duration-300'
                              } group flex items-center w-full px-2 py-2 text-[12px] font-open-sans capitalize transition-all`}
                              type="button"
                              onClick={() => {}}
                              tabIndex={0}
                            >
                              {selectedList.some(
                                (current) => current.value === option.value
                              ) ? (
                                <MultiSelectDisabledIcon />
                              ) : (
                                <MultiSelectIconBlank />
                              )}
                              <span className="ml-1">{option.name}</span>
                            </button>
                          ) : (
                            <button
                              className={` flex justify-start items-center ${
                                active &&
                                'bg-thryv-white-300 transition-all duration-300'
                              } group flex items-center w-full px-2 py-2 text-[12px] font-open-sans capitalize transition-all`}
                              onClick={(e) => {
                                handleOnclick(option, e);
                              }}
                              type="button"
                              tabIndex={0}
                            >
                              {selectedList.some(
                                (current) => current.value === option.value
                              ) ? (
                                <MultiSelectIconFilled />
                              ) : (
                                <MultiSelectIconBlank />
                              )}
                              <span className="ml-1">{option.name}</span>
                            </button>
                          )
                        }
                      </Listbox.Option>
                    </div>
                  ))}
                </div>
                {(!maximum || maximum === options?.length) &&
                !disableSelectAll ? (
                  <div className="sticky bottom-0 bg-white z-10 w-full border-t border-thryv-gray-light-500">
                    <Listbox.Option value={{ name: 'All', value: 'All' }}>
                      {({ active }) =>
                        disabled ? (
                          <button
                            className={`flex justify-start items-center ${
                              active &&
                              'bg-thryv-white-300 transition-all duration-300'
                            } group flex items-center w-full px-2 py-2 text-[12px] font-open-sans capitalize transition-all`}
                            type="button"
                            onClick={() => {}}
                            tabIndex={0}
                          >
                            {selectedList?.length === options?.length ? (
                              <MultiSelectDisabledIcon />
                            ) : (
                              <MultiSelectIconBlank />
                            )}
                            <span className="ml-1">All</span>
                          </button>
                        ) : (
                          <button
                            className={` flex justify-start items-center ${
                              active &&
                              'bg-thryv-white-300 transition-all duration-300'
                            } group flex items-center w-full px-2 py-2 text-[12px] font-open-sans capitalize transition-all`}
                            onClick={(e) => {
                              handleOnclick({ name: 'All', value: 'All' }, e);
                            }}
                            tabIndex={0}
                          >
                            {selectedList?.length === options?.length ? (
                              <MultiSelectIconFilled />
                            ) : (
                              <MultiSelectIconBlank />
                            )}
                            <span className="ml-1">All</span>
                          </button>
                        )
                      }
                    </Listbox.Option>
                  </div>
                ) : null}
              </Listbox.Options>
            </>
          </Transition>
        </>
      </Listbox>
      {errors && name && errors[name] && errors[name].message && (
        <ErrorMessage errorMessage={errors[name].message} />
      )}
      {!isValid && errorMessage && <ErrorMessage errorMessage={errorMessage} />}
    </div>
  );
};
